<script setup>
import { reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: value => ['edit', 'create'].includes(value),
  },
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('captainAssistants/getUIFlags'),
};

const initialState = {
  name: '',
  description: '',
  productName: '',
  featureFaq: false,
  featureMemory: false,
  featureCitation: false,
};

const state = reactive({ ...initialState });

const validationRules = {
  name: { required, minLength: minLength(1) },
  description: { required, minLength: minLength(1) },
  productName: { required, minLength: minLength(1) },
};

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(() => formState.uiFlags.value.creatingItem);

const getErrorMessage = (field, errorKey) => {
  return v$.value[field].$error
    ? t(`CAPTAIN.ASSISTANTS.FORM.${errorKey}.ERROR`)
    : '';
};

const formErrors = computed(() => ({
  name: getErrorMessage('name', 'NAME'),
  description: getErrorMessage('description', 'DESCRIPTION'),
  productName: getErrorMessage('productName', 'PRODUCT_NAME'),
}));

// Label column on the start edge, control on the end edge, stacked below `sm`.
const fieldRowClass =
  'grid gap-1.5 sm:grid-cols-[8rem_minmax(0,1fr)] sm:gap-x-6 sm:gap-y-0';
const labelClass = 'text-sm font-medium text-n-slate-12 sm:pt-2.5';

const handleCancel = () => emit('cancel');

const prepareAssistantDetails = () => ({
  name: state.name,
  description: state.description,
  // config is a jsonb column the API replaces wholesale, so keep the keys this
  // form does not own (enabled skills, system messages) instead of dropping them.
  config: {
    ...props.assistant?.config,
    product_name: state.productName,
    feature_faq: state.featureFaq,
    feature_memory: state.featureMemory,
    feature_citation: state.featureCitation,
  },
});

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) {
    return;
  }

  emit('submit', prepareAssistantDetails());
};

const updateStateFromAssistant = assistant => {
  if (!assistant) return;

  const { name, description, config } = assistant;

  Object.assign(state, {
    name,
    description,
    productName: config.product_name,
    featureFaq: config.feature_faq || false,
    featureMemory: config.feature_memory || false,
    featureCitation: config.feature_citation || false,
  });
};

watch(
  () => props.assistant,
  newAssistant => {
    if (props.mode === 'edit' && newAssistant) {
      updateStateFromAssistant(newAssistant);
    }
  },
  { immediate: true }
);
</script>

<template>
  <form class="flex flex-col gap-5" @submit.prevent="handleSubmit">
    <div :class="fieldRowClass">
      <label for="assistant-name" :class="labelClass">
        {{ t('CAPTAIN.ASSISTANTS.FORM.NAME.LABEL') }}
      </label>
      <Input
        id="assistant-name"
        v-model="state.name"
        :placeholder="t('CAPTAIN.ASSISTANTS.FORM.NAME.PLACEHOLDER')"
        :message="formErrors.name"
        :message-type="formErrors.name ? 'error' : 'info'"
      />
    </div>

    <div :class="fieldRowClass">
      <label :class="labelClass">
        {{ t('CAPTAIN.ASSISTANTS.FORM.DESCRIPTION.LABEL') }}
      </label>
      <Editor
        v-model="state.description"
        :max-length="500"
        :placeholder="t('CAPTAIN.ASSISTANTS.FORM.DESCRIPTION.PLACEHOLDER')"
        :message="formErrors.description"
        :message-type="formErrors.description ? 'error' : 'info'"
      />
    </div>

    <div :class="fieldRowClass">
      <label for="assistant-product-name" :class="labelClass">
        {{ t('CAPTAIN.ASSISTANTS.FORM.PRODUCT_NAME.LABEL') }}
      </label>
      <Input
        id="assistant-product-name"
        v-model="state.productName"
        :placeholder="t('CAPTAIN.ASSISTANTS.FORM.PRODUCT_NAME.PLACEHOLDER')"
        :message="formErrors.productName"
        :message-type="formErrors.productName ? 'error' : 'info'"
      />
    </div>

    <div
      :class="fieldRowClass"
      role="group"
      aria-labelledby="assistant-features-label"
    >
      <span id="assistant-features-label" :class="labelClass">
        {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.TITLE') }}
      </span>

      <div class="flex flex-col gap-2.5">
        <label class="flex items-start gap-2">
          <input v-model="state.featureFaq" type="checkbox" class="mt-0.5" />
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_CONVERSATION_FAQS') }}
          </span>
        </label>

        <label class="flex items-start gap-2">
          <input v-model="state.featureMemory" type="checkbox" class="mt-0.5" />
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_MEMORIES') }}
          </span>
        </label>

        <label class="flex items-start gap-2">
          <input
            v-model="state.featureCitation"
            type="checkbox"
            class="mt-0.5"
          />
          <span class="text-sm text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_CITATIONS') }}
          </span>
        </label>
      </div>
    </div>

    <div
      class="flex items-center justify-end w-full gap-3 pt-5 border-t border-n-weak"
    >
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('CAPTAIN.FORM.CANCEL')"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="t(`CAPTAIN.FORM.${mode.toUpperCase()}`)"
        :is-loading="isLoading"
        :disabled="isLoading"
      />
    </div>
  </form>
</template>
